import { useEffect, useState } from 'react'
import { useAppStore } from '../../store/app'
import { getHouseholdHandbook, setHouseholdHandbook } from '../../api/ws'
import { SectionHeader, Group } from '../account/AccountComponents'
import '../account/AccountStyles.css'
import './HandbookSection.css'

// The household handbook — how things are done in this house
// (docs/introductions.md §3.2).
//
// The roster (who works here) is derived from the database. This is the half
// nothing can derive: quiet hours, "ask before spending money", who handles the
// school run. Every agent in the household reads it, which is the point and also
// the warning below the box.
//
// Fetched on demand, not from the store: it is content, potentially kilobytes,
// and does not belong on a list that goes out on every connect.
export function HandbookSection() {
  const organizationID = useAppStore((s) => s.organizationID)

  const [draft, setDraft] = useState('')
  const [saved, setSaved] = useState('')
  const [maxBytes, setMaxBytes] = useState(16 * 1024)
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!organizationID) return
    let cancelled = false
    getHouseholdHandbook(organizationID)
      .then((r) => {
        if (cancelled || r.error) return
        setDraft(r.handbook ?? '')
        setSaved(r.handbook ?? '')
        if (r.max_bytes) setMaxBytes(r.max_bytes)
        setLoaded(true)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [organizationID])

  // Bytes, not characters — the server's cap is on the encoded length, and an
  // emoji or an accented name costs more than one. Counting characters here
  // would let someone hit a refusal the counter said was impossible.
  const used = new TextEncoder().encode(draft).length
  const tooLong = used > maxBytes
  const dirty = draft !== saved

  function save() {
    if (!organizationID || saving || tooLong) return
    setSaving(true)
    setError(null)
    setHouseholdHandbook(organizationID, draft)
      .then((r) => {
        if (r.error) setError(r.error)
        else setSaved(r.handbook ?? draft)
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setSaving(false))
  }

  if (!organizationID) return null

  return (
    <>
      <SectionHeader>House handbook</SectionHeader>
      <Group>
        <div className="handbook">
          <p className="handbook__hint">
            How things are done here — quiet hours, who handles what, anything
            you'd tell someone on their first day. Everyone who works here reads
            this, so leave out anything meant for one person only.
          </p>
          <textarea
            className="handbook__body"
            value={draft}
            placeholder={
              loaded
                ? "e.g. Quiet after 9pm. Ask before spending anything over £50. The school run is Sam's."
                : 'Loading…'
            }
            rows={10}
            disabled={!loaded || saving}
            aria-label="House handbook"
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="handbook__footer">
            <span className={`handbook__count${tooLong ? ' handbook__count--over' : ''}`}>
              {tooLong
                ? `${(used - maxBytes).toLocaleString()} too long`
                : `${used.toLocaleString()} / ${maxBytes.toLocaleString()}`}
            </span>
            <button
              className="handbook__save"
              onClick={save}
              disabled={!loaded || saving || tooLong || !dirty}
            >
              {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
            </button>
          </div>
          {error && <div className="handbook__error">{error}</div>}
        </div>
      </Group>
    </>
  )
}
